<template>
  <v-layout row wrap>
    <v-flex md12>
      <header class="hvh-content-header">
        <div class="ml-2 mb-4">
          <h1 class="hvh-h1">Cohorts</h1>
        </div>
      </header>
    </v-flex>

    <v-flex md12 class="d-flex mb-4">
      <v-flex xs8>
        <v-btn class="hvh-btn hvh-btn--secondary" :class="{ disabled: !checkedCohortItemsWithVersions.length }" flat @click="deleteDialogVisible = true">Archive</v-btn>
        <v-btn class="hvh-btn hvh-btn--secondary" :class="{ disabled: !checkedCohortItemsWithVersions.length }" flat @click="openShareDialog(checkedCohortItemsWithVersions)">Share</v-btn>
        <v-btn class="hvh-btn hvh-btn--secondary" :class="{ disabled: !checkedCohortItems.length }" flat @click="setAutoRefresh(checkedCohortItems, false)">Disable Cohort Refresh</v-btn>
      </v-flex>
      <v-flex xs4>
        <v-text-field v-model="fieldSearchText" outline height="0" :full-width="true" append-icon="mdi-magnify" label="Search cohorts" hide-details @keydown.enter="changeSearchText" @keydown.esc="clearSearchText"></v-text-field>
      </v-flex>
    </v-flex>
    <v-flex class="hvh-card--rounded">
      <v-data-table :headers="headers" :items="cohorts" :search="searchText" :customFilter="customFilter" must-sort :pagination.sync="pagination" ref="dt">
        <template slot="headers">
          <tr>
            <th>
              <hvh-input-checkbox :value="isAllChecked" @input="toggleCheckAll" :color="getCheckAllColor"></hvh-input-checkbox>
            </th>
            <dt-th v-for="(header, ind) in headers" :key="ind" :class="header.class" :value="header.value" :pagination.sync="pagination">{{ header.text }}</dt-th>
          </tr>
        </template>
        <template slot="items" slot-scope="props">
          <tr>
            <td>
              <hvh-input-checkbox v-model="props.item.checked" @input="uncheckOthers" color="#11697d"></hvh-input-checkbox>
            </td>
            <td @click="expand(props)">{{ props.item.name }}</td>
            <td @click="expand(props)">{{ props.item.patientDatasource }}</td>
            <td class="text-xs-right" @click="expand(props)">{{ (props.item.cohortStatistic || {}).totalCount }}</td>
            <td>
              <span class="hvh-date">{{ props.item.creationDate | formatDate }}</span>
            </td>
            <td class="text-xs-right width-100px" @click="expand(props)">{{ props.item.numberOfRefreshes }}</td>
            <td>
              <hvh-input-switch :value="props.item.autoRefresh" @input="setAutoRefresh([props.item], $event)"></hvh-input-switch>
            </td>
            <td class="text-xs-right">
              <v-btn flat class="hvh-txt-btn" @click="open('/Analytics/Cohort/' + props.item.id + '/Overview')" @contextmenu.prevent="handleRightClick($event, props.item)">
                <v-icon left small class="mr-1">mdi-eye</v-icon>
                <span>View</span>
              </v-btn>
            </td>
          </tr>
          <template v-if="props.expanded">
            <tr class="gray-background" v-if="!cohortVersions">
              <td colspan="8">Loading...</td>
            </tr>
            <template v-else>
              <tr class="gray-background" v-if="!cohortVersions.length">
                <td colspan="8">No other versions to show.</td>
              </tr>
              <tr class="gray-background" v-for="(cohort, ind) in cohortVersions" :key="ind">
                <td>
                  <hvh-input-checkbox v-model="cohort.checked" @input="cohortVersions.__ob__.dep.notify()" color="#11697d"></hvh-input-checkbox>
                </td>
                <td>{{ cohort.name }}</td>
                <td>{{ cohort.patientDatasource }}</td>
                <td class="text-xs-right">{{ (cohort.cohortStatistic || {}).totalCount }}</td>
                <td>
                  <span class="hvh-date">{{ cohort.creationDate | formatDate }}</span>
                </td>
                <td></td>
                <td></td>
              </tr>
            </template>
          </template>
        </template>
        <div slot="no-data" :value="true" type="warning">There are no items to show.</div>
      </v-data-table>
    </v-flex>
    <!-- Pop-up Dialog for deleting cohorts -->
    <hvh-modal v-model="deleteDialogVisible" title="Archive Cohorts" max-width="700">
      <template v-slot>
        <p>
          You are about to archive {{ checkedCohortItemsWithVersions.length }} cohorts. <br /><br />
          This action cannot be undone. Do you still want to archive cohorts?
        </p>
      </template>
      <template v-slot:actions>
        <v-btn flat class="hvh-btn hvh-btn--secondary" @click="deleteDialogVisible = false">Cancel</v-btn>
        <v-btn flat class="hvh-btn hvh-btn--secondary" @click="deleteAllVersions(checkedCohortItemsWithVersions)">Archive All Versions</v-btn>
        <v-btn flat class="hvh-btn hvh-btn--primary" @click="deleteCohorts(checkedCohortItemsWithVersions)">Archive Current Version</v-btn>
      </template>
    </hvh-modal>

    <!-- Pop-up Dialog for sharing cohorts -->
    <hvh-modal v-model="shareDialogVisible" title="Share Cohorts">
      <template v-slot>
        <hvh-share-cohort instructional-text="Sharing a Cohort will give selected users read and/or write access to the Cohort." :users="userShares"></hvh-share-cohort>
      </template>
      <template v-slot:actions>
        <v-btn @click="shareCohorts(checkedCohortItemsWithVersions)" flat class="hvh-btn hvh-btn--primary">Share Cohorts</v-btn>
      </template>
    </hvh-modal>

    <!-- Right click menu -->
    <hvh-context-menu :event="activeContextMenuEvent">
      <li @click="open(activeLink)">
        <label>Open</label>
      </li>
      <li @click="openInNewTab(activeLink)">
        <label>Open link in new tab</label>
      </li>
    </hvh-context-menu>
  </v-layout>
</template>  

<script>
import axios from '../../../axios';
import { createFormData, errorDebug } from '../../../libraries/common';
export default {
  noExport: true,
  props: ['cohorts', 'usersForShare'],
  data() {
    return {
      headers: [
        { text: 'Name', align: 'left', value: 'name', class: ['text-xs-left hvh-table-header width-40'] },
        { text: 'Dataset', align: 'left', value: 'patientDatasource', class: ['text-xs-left hvh-table-header'] },
        { text: 'Patient Count', align: 'left', value: 'cohortStatistic.totalCount', class: ['text-xs-left hvh-table-header width-100px'] },
        { text: 'Last Update', align: 'left', value: 'creationDate', class: ['text-xs-left hvh-table-header'] },
        { text: 'Number of Refreshes', align: 'left', value: 'numberOfRefreshes', class: ['text-xs-left hvh-table-header width-100px'] },
        { text: 'Auto Refresh', align: 'left', value: 'autoRefresh', class: ['text-xs-left hvh-table-header'] },
        { text: '', align: 'left', class: ['text-xs-left hvh-table-header'] },
      ],
      pagination: { 'sortBy': 'creationDate', 'descending': true, 'rowsPerPage': 25 },
      searchText: '',
      fieldSearchText: '',
      deleteDialogVisible: false,
      shareDialogVisible: false,
      userShares: [],
      cachedVersions: {},
      cohortVersions: null,
      activeContextMenuEvent: null,
      activeLink: null,
    };
  },
  computed: {
    checkedCohortItems() {
      return this.cohorts.filter(f => f.checked);
    },
    checkedCohortItemsWithVersions() {
      return this.checkedCohortItems.concat((this.cohortVersions || []).filter(f => f.checked));
    },
    getCheckAllColor() {
      return this.cohorts.all(f => f.checked) ? '#11697d' : 'grey';
    },
    isAllChecked() {
      return this.cohorts.any(f => f.checked);
    },
  },
  methods: {
    setAutoRefresh(cohorts, event) {
      axios.post(`/Analytics/Cohort/SetAutoRefresh`, createFormData({ cohortIds: cohorts.map(f => f.id), value: event }))
        .then(() => cohorts.forEach(f => f.autoRefresh = event))
        .catch(errorDebug);
    },
    deleteCohorts(cohorts) {
      var cohortVersions = this.cohortVersions;
      axios.post(`/Analytics/Cohort/Archive`, createFormData({ cohortIds: cohorts.map(f => f.id) }))
        .then(() => {
          this.deleteDialogVisible = false;
          var lastVersionsRemoved = this.cohorts.filter(f => cohorts.indexOf(f) > -1);
          this.cohorts = this.cohorts.filter(f => cohorts.indexOf(f) === -1);
          if (cohortVersions) {
            cohorts.forEach(f => {
              var ind = cohortVersions.indexOf(f);
              if (ind > -1)
                cohortVersions.splice(ind, 1);
            });
          }

          if (lastVersionsRemoved.length)
            axios.get('/Analytics/BulkEdit/Cohorts?versionGroupIds=' + lastVersionsRemoved.map(f => f.versionGroupId).join(','))
              .then(response => {
                response.data.cohorts.forEach(f => f.numberOfRefreshes = response.data.numberOfRefreshes[f.versionGroupId] || 0);
                this.cohorts = this.cohorts.concat(response.data.cohorts);
              });
        })
        .catch(errorDebug);
    },
    deleteAllVersions(cohorts) {
      var versionIds = cohorts.groupBy('versionGroupId').map(f => f.key);

      axios.post(`/Analytics/Cohort/ArchiveAllVersions`, createFormData({ versionGroupIds: versionIds.join(',') }))
        .then(() => {
          this.deleteDialogVisible = false;
          this.cohorts = this.cohorts.filter(f => versionIds.find(g => g == f.versionGroupId) == null);
        }).catch(errorDebug);
    },
    expand(props) {
      props.expanded = !props.expanded;
      this.cohortVersions = null;

      if (props.expanded) {
        var setCohortVersions = () => {
          this.cohortVersions = this.cachedVersions[props.item.versionGroupId]
            .filter(f => f.id !== props.item.id);
          this.cohortVersions.forEach(f => f.checked = false);
        };

        if (!this.cachedVersions[props.item.versionGroupId]) {
          this.cachedVersions[props.item.versionGroupId] = {};

          axios.get(`/Analytics/Cohort/${props.item.id}/GetAllVersions`)
            .then(response => {
              var ind = response.data.indexOf(response.data.find(f => f.id === props.item.id));
              response.data[ind] = props.item;

              this.cachedVersions[props.item.versionGroupId] = response.data;
              setCohortVersions();
            })
            .catch(errorDebug);
        }
        else setCohortVersions();
      }
    },
    openShareDialog(cohorts) {
      axios.get(`/Analytics/BulkEdit/CohortsShares?cohortIds=${cohorts.map(f => f.id)}`)
        .then(response => {
          var rights = Object.values(response.data).flatMap(f => f);
          this.userShares = this.usersForShare
            .map((f, ind) => {
              var access = rights.filter(g => g.user.email === f.email);
              var maxAccess = access.length ? access.sortBy('access')[0].access : null;

              return ({
                ...f,
                id: ind,
                access: maxAccess || Math.max(1, f.maxRight),
                selected: maxAccess != null || f.isAdmin,
                //halfChecked if all are not the same
                halfChecked: access.any() && (!cohorts.all(cohort => (response.data[cohort.id] || []).any(g => g.user.email === f.email)) || !access.all(ac => ac.access === (maxAccess || 1))),
              });
            });
          this.shareDialogVisible = true;
        })
        .catch(errorDebug);
    },
    shareCohorts(cohorts) {
      var selectedUsers = this.userShares.filter(f => f.selected && !f.isAdmin)
        .map(f => ({ ...f, notChanged: f.halfChecked }));

      axios.post('/Analytics/Cohort/Share', { ids: cohorts.map(f => f.id), userShares: selectedUsers })
        .then(n => {
          if (!n.data.isSuccess) {
            alert(n.data.message)
          }
          this.shareDialogVisible = false;
        });
    },
    handleRightClick(event, item) {
      this.activeLink = '/Analytics/Cohort/' + item.id + '/Overview';
      this.activeContextMenuEvent = event;
    },
    customFilter(items, search) {
      var searchText = search.toLowerCase();
      return items.filter(f => f.name.toLowerCase().indexOf(searchText) > -1
        || f.patientDatasource.toLowerCase().indexOf(searchText) > -1
        || (f.createdBy || '').toLowerCase().indexOf(searchText) > -1
      );
    },
    changeSearchText() {
      this.searchText = this.fieldSearchText;
    },
    clearSearchText() {
      this.fieldSearchText = '';
      this.searchText = '';
    },
    toggleCheckAll() {
      var filteredItems = this.$refs.dt.filteredItems;
      var shouldUncheckAll = this.cohorts.all(f => f.checked);
      var shouldCheckAll = filteredItems.all(f => f.checked);

      this.cohorts.forEach(f => this.$set(f, 'checked', false));
      if (!shouldUncheckAll) {
        var items = shouldCheckAll ? this.cohorts : filteredItems;
        items.forEach(f => this.$set(f, 'checked', true));
      }
    },
    uncheckOthers() {
      var filteredItems = this.$refs.dt.filteredItems;
      this.cohorts.filter(f => filteredItems.indexOf(f) === -1)
        .forEach(f => this.$set(f, 'checked', false));
    },
  },
}
</script>
